"use client";
import { Button } from "@/components/ui/button";
import {
    IAccountAssets,
    IBlacklistUrls,
    IBlocks,
    ITheme,
    IUserData,
} from "@/types";
import React from "react";
import { BlockUpdateFunction } from ".";
import Link from "next/link";
import AdminPopover from "../../content/AdminPopover";
import LinkOptions from "./tabs/link/LinkOptions";
import LinkCustomization from "./tabs/link/LinkCustomization";
import { shadow } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import BlockToggle from "./tabs/toggle";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import BlockMetadata from "./tabs/metadata";
import IconAssetPicker from "./tabs/iconAssetPicker";
import RenderIconOrAsset from "../RenderIconOrAsset";

interface Props {
    admin?: boolean;
    user: IUserData;
    block: IBlocks;
    update: BlockUpdateFunction;
    style: any;
    assets: IAccountAssets[];
    theme: ITheme;
    newBlock?: string | null;
    blackListUrls?: IBlacklistUrls[] | null;
}
export default function BlockLink({
    admin,
    user,
    block,
    update,
    style,
    assets,
    theme,
    newBlock,
    blackListUrls,
}: Props) {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    const contentWarning = blackListUrls?.find((e) =>
        blockUpdate.url?.includes(e.find)
    );
    const BlockClickHandler = (button: React.MouseEvent, block: IBlocks) => {
        if ((button.button == 0 && !admin) || (button.button == 1 && !admin)) {
            if (
                block.type == "divider" ||
                block.type == "blog_post_all" ||
                block.type === "carousel" ||
                block.type === "vrcdn_live" ||
                block.type === "rich_text_block"
            )
                return;
            fetch("/api/user/click", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    account_id: user.id,
                    block_id: block.id,
                }),
            });
        }
    };
    React.useEffect(() => {
        if (newBlock === block.id) {
            handleExpand();
        }
        return () => {
            window.removeEventListener("closeBlock", handleClose);
        };
    }, [newBlock, block.id]);
    React.useEffect(() => {
        window.addEventListener("closeBlock", {
            handleEvent: (event: CustomEvent) => {
                if (event.detail.id != block.id) {
                    if (isExpanded) {
                        setIsExpanded(false);
                    }
                }
            },
        });
    });
    React.useEffect(() => {
        if (block != blockUpdate) {
            setBlockUpdate(block);
        }
    }, [block]);
    const handleClose = () => {
        setIsExpanded(false);
        window.dispatchEvent(new CustomEvent("collapseBlock"));
    };
    function handleUpdate(value: IBlocks) {
        if (value == null) {
            update(blockUpdate.id, value, true);
            return;
        }
        setBlockUpdate({
            ...blockUpdate,
            ...value,
        });
        if (block == value) return;
        update(blockUpdate.id, value);
    }
    const event = new CustomEvent("expandBlock", {
        detail: {
            id: blockUpdate.id,
            title: "Link",
            titleOptions: (
                <BlockToggle
                    block={blockUpdate}
                    blockUpdate={setBlockUpdate}
                    update={handleUpdate}
                />
            ),
            content: (
                <div className="flex flex-col gap-3 pb-3">
                    <LinkOptions
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                        account={user}
                    />
                    {/* <Separator /> */}
                    <IconAssetPicker
                        account={user}
                        block={block}
                        update={handleUpdate}
                        assets={assets}
                    />
                    {/* <Separator /> */}
                    <LinkCustomization
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                        assets={assets}
                        user={user}
                    />
                    {/* <Separator /> */}
                    <BlockMetadata block={blockUpdate} update={handleUpdate} />
                </div>
            ),
        },
    });
    const handleExpand = () => {
        window.dispatchEvent(
            new CustomEvent("closeBlock", {
                detail: {
                    id: block.id,
                },
            })
        );
        setIsExpanded(true);
        window.dispatchEvent(event);
    };
    React.useEffect(() => {
        if (isExpanded) {
            window.dispatchEvent(event);
        }
    }, [blockUpdate]);
    return (
        <AdminPopover
            admin={admin}
            className={block.show ? "" : "opacity-30"}
            onClick={handleExpand}
            title={"Link | " + block.url}
            isOpen={isExpanded}
            onClose={handleClose}
        >
            {blockUpdate.adult || contentWarning ? (
                <Dialog>
                    <DialogTrigger asChild>
                        <div className={`w-full`}>
                            <button
                                style={style}
                                id="block"
                                className={`{formatClassName(text)} 
                ${shadow(theme.button_shadow)}
                ${theme?.button_rounded}
                w-full 
                px-1 
                py-3 text-xl
                 transition-all 
                duration-150 hover:scale-105
                ${block.name.replaceAll(" ", "-")}
                `}
                            >
                                <RenderIconOrAsset
                                    account_id={user.id}
                                    iconName={blockUpdate.icon}
                                    size={26}
                                />
                                <span
                                    id="block-text"
                                    className={`block w-full px-6 text-center font-bold`}
                                >
                                    {blockUpdate.name}
                                </span>
                            </button>
                        </div>
                    </DialogTrigger>
                    <DialogContent className="sm:h-fit">
                        <DialogHeader>
                            <DialogTitle>
                                NSFW or mature content ahead
                            </DialogTitle>
                            <DialogDescription>
                                Where you're about to go has been flagged as
                                mature or NSFW
                            </DialogDescription>
                        </DialogHeader>
                        <div className="flex flex-col gap-4">
                            <p className="w-full rounded-md bg-accent p-1 text-center text-sm text-foreground/80">
                                {blockUpdate.url.replace("https://", "")}
                            </p>
                            <Separator />
                            <Label variant={"toggle"}>
                                Would you like to continue?
                            </Label>
                            <div className="flex w-full items-center gap-2">
                                <Link
                                    href={blockUpdate.url}
                                    target="_blank"
                                    prefetch={false}
                                    onClick={(e) => {
                                        BlockClickHandler(e, block);
                                    }}
                                    onAuxClick={(e) => {
                                        BlockClickHandler(e, block);
                                    }}
                                >
                                    <Button>Proceed</Button>
                                </Link>
                                <DialogClose>
                                    <Button variant={"outline"}>Go back</Button>
                                </DialogClose>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            ) : (
                <Link
                    className={`w-full`}
                    href={blockUpdate.url}
                    target="_blank"
                    prefetch={false}
                >
                    <button
                        style={style}
                        id="block"
                        className={`{formatClassName(text)} 
                ${shadow(theme.button_shadow)}
                ${theme?.button_rounded}
                w-full 
                px-1 
                py-3 text-xl
                 transition-all 
                duration-150 hover:scale-105
                ${block.name.replaceAll(" ", "-")}
                `}
                    >
                        <RenderIconOrAsset
                            account_id={user.id}
                            iconName={blockUpdate.icon}
                            size={26}
                        />
                        <span
                            id="block-text"
                            className={`block w-full px-6 text-center font-bold`}
                        >
                            {blockUpdate.name}
                        </span>
                    </button>
                </Link>
            )}
        </AdminPopover>
    );
}
